import React, { createContext, useState, useContext, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';

import { DB } from '~/services/firebase';
import ISupplie from '~/interfaces/ISupplie';

interface SuppliesContextData {
  supplies: ISupplie[];
  loadSupplies(id: string): Promise<void>;
  removeSupplie(id: string): void;
  clearContext(): void;
  isInitialized: boolean;
}

const SuppliesContext = createContext<SuppliesContextData>(
  {} as SuppliesContextData,
);

export const SuppliesProvider: React.FC = ({ children }) => {
  const [supplies, setSupplies] = useState<ISupplie[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const clearContext = useCallback(() => {
    setSupplies([]);
    setIsInitialized(false);
  }, []);

  const removeSupplie = useCallback(
    (id: string) => {
      const filter = supplies.filter(item => item.uid !== id);
      setSupplies(filter);
    },
    [supplies],
  );

  const loadSupplies = useCallback(async (id: string) => {
    const q = query(collection(DB, 'supplies'), where('company_id', '==', id));
    const querySnapshot = await getDocs(q);

    const items: ISupplie[] = [];

    querySnapshot.docs.forEach(item => {
      const parseItem = {
        ...item.data(),
        uid: item.id,
      } as ISupplie;

      items.push(parseItem);
    });

    setSupplies(items);
    setIsInitialized(true);
  }, []);

  return (
    <SuppliesContext.Provider
      value={{
        supplies,
        loadSupplies,
        clearContext,
        removeSupplie,
        isInitialized,
      }}
    >
      {children}
    </SuppliesContext.Provider>
  );
};

export function useSupplies(): SuppliesContextData {
  const context = useContext(SuppliesContext);
  return context;
}
